import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { rich } from '../../shared/rich-text.tsx'
import { Card } from '@rs/ui'
import type { MyLegRow } from './api.ts'

/**
 * ══ CỔNG THẦU PHỤ ▸ 📊 BÁO CÁO — chép `NccPortal.tsx:1003` ═══════════════════════════════════
 *
 * ⭐ ĐỌC TRÊN CHÍNH `myLegs` ĐÃ TẢI, không gọi thêm cửa nào. Máy chủ đã lọc danh sách theo đúng
 *   thầu phụ đang đăng nhập (`fetchMyLegs`), nên bốn con số ở đây không có đường nào chạm vào
 *   chuyến của thầu phụ khác — và cũng không cần một cửa "báo cáo" thứ hai để lọc lại lần nữa.
 *
 * ⚠ KHÔNG CÓ Ô TIỀN NÀO. Cùng hợp đồng với cả cổng: `VendorLegDto` không mang giá vốn, và bản cũ
 *   cũng giấu hẳn Tổng thu/Tổng phí ở đây (`NccPortal.tsx:727`, ghi rõ "bảo mật"). Một thẻ KPI
 *   tiền ở màn này là chỗ rò giá vốn ra ngoài công ty.
 * ⚠ "CHỜ NHẬN" = chưa xác nhận VÀ chưa từ chối. Lấy `tổng − đã nhận` thì chuyến bị từ chối lọt vào
 *   ô chờ, và thầu phụ tưởng mình còn việc phải bấm.
 */

export interface VendorReport {
  /** ⚠ `legCount`, KHÔNG phải `total`: cổng lint cấm tên tiền kiểu `number`, và đây là ĐẾM CHUYẾN —
   *  đặt đúng tên cũng là nói rõ màn này không có ô tiền nào (xem chú thích đầu tệp). */
  legCount: number
  confirmed: number
  waiting: number
  rejected: number
  /** `[YYYY-MM, số chuyến]`, tháng MỚI NHẤT lên trước. */
  byMonth: [string, number][]
}

export const vendorReport = (legs: readonly MyLegRow[]): VendorReport => {
  const months = new Map<string, number>()
  for (const l of legs) {
    const key = l.planDate.slice(0, 7)
    months.set(key, (months.get(key) ?? 0) + 1)
  }
  return {
    legCount: legs.length,
    confirmed: legs.filter((l) => l.vendorConfirmed && !l.vendorRejected).length,
    waiting: legs.filter((l) => !l.vendorConfirmed && !l.vendorRejected).length,
    rejected: legs.filter((l) => l.vendorRejected).length,
    byMonth: [...months.entries()].sort((a, b) => b[0].localeCompare(a[0])),
  }
}

export const ReportTab = ({ legs, vendorName }: {
  legs: readonly MyLegRow[]
  vendorName: string
}): JSX.Element => {
  const { t } = useTranslation()
  const r = vendorReport(legs)

  return (
    <div className="space-y-3">
      {/* Bốn thẻ viền màu — đúng bản cũ (`NccPortal.tsx:1007-1011`). */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
          <div className="text-lg font-bold tabular-nums text-slate-700">{r.legCount}</div>
          <div className="text-[10px] font-semibold uppercase text-slate-500">{t('ncc.kpiTotal')}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
          <div className="text-lg font-bold tabular-nums text-emerald-600">{r.confirmed}</div>
          <div className="text-[10px] font-semibold uppercase text-emerald-700">{t('ncc.rpConfirmed')}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center">
          <div className="text-lg font-bold tabular-nums text-amber-600">{r.waiting}</div>
          <div className="text-[10px] font-semibold uppercase text-amber-700">{t('ncc.rpWaiting')}</div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-center">
          <div className="text-lg font-bold tabular-nums text-red-600">{r.rejected}</div>
          <div className="text-[10px] font-semibold uppercase text-red-700">{t('ncc.rpRejected')}</div>
        </div>
      </div>

      <Card className="!p-0 overflow-hidden">
        <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase text-slate-600">
          {t('ncc.byMonth')}
        </div>
        {r.byMonth.length === 0
          ? <div className="p-6 text-center text-sm italic text-slate-400">{t('ncc.emptyMonth')}</div>
          : (
            <div className="divide-y divide-slate-100">
              {r.byMonth.map(([key, n]) => (
                <div key={key} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-600">
                    {t('ncc.monthLabel', { m: key.slice(5), y: key.slice(0, 4) })}
                  </span>
                  <span className="font-bold tabular-nums text-slate-700">{t('ncc.legUnit', { n })}</span>
                </div>
              ))}
            </div>
          )}
      </Card>

      <p className="text-[11px] italic text-slate-400">{rich(t('ncc.reportNote', { name: vendorName }))}</p>
    </div>
  )
}
